import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import messages from './messages'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import {
  Banner,
  Button,
  Checkbox,
  Chip,
  DataTable,
  Input,
  MarkdownTextArea,
  Modal,
  Select,
  SelectAsyncInline,
} from '@arnsbogroup/react-voicearchive'
import DateTime from 'react-datetime'
import debounce from 'debounce-promise'
import SupplierSelect from 'components/SupplierSelect'
import useApi from 'hooks/useApi'
import useCurrentUser from 'hooks/useCurrentUser'

// Relations are resolved into objects, unassigned ones come back as a string
const resolveSupplier = (supplier) => (
  supplier && typeof supplier === 'object' && +supplier.uid > 0 ? supplier : null
)

const assignedSupplier = (task) => (
  resolveSupplier(task?.supplier) || resolveSupplier(task?.preferredSupplier)
)

const versionLabel = (version) => (
  version?.title
    || version?.targetlanguage?.name
    || (typeof version?.targetlanguage === 'string' ? version.targetlanguage : '')
    || `Version ${version?.uid}`
)

// The naive wall clock the back end resolves in the server timezone
const toServerDateTime = (value) => {
  const date = new Date(value)

  if (isNaN(date.getTime())) {
    return null
  }

  const local = new Date(date)
  local.setMinutes(local.getMinutes() - local.getTimezoneOffset())

  return local.toJSON().slice(0, 19).replace('T', ' ')
}

const loadCurrencies = debounce((query) => (
  fetch(`/rest/v1/currency?search[iso]=${query}`)
    .then((response) => response.json())
    .then((data) => data.map(({ uid, iso }) => ({ value: uid, label: iso })))
), 250)

export default NiceModal.create(({ order, selectedTasks = [] }) => {
  const modal = useModal()
  const { formatMessage, formatDate } = useIntl()
  const { roles = [] } = useCurrentUser()

  const [loading, setLoading] = useState(false)
  const [supplier, setSupplier] = useState(null)
  const [brief, setBrief] = useState('')
  const [cost, setCost] = useState('')
  const [price, setPrice] = useState('')
  const [deadline, setDeadline] = useState('')
  const [fileSpecs, setFileSpecs] = useState([])
  const [supplierTypeIds, setSupplierTypeIds] = useState([])

  const [costCurrency, setCostCurrency] = useState(null)
  const [priceCurrency, setPriceCurrency] = useState(() => (
    order?.customer?.currency
      ? { value: order.customer.currency.uid, label: order.customer.currency.iso }
      : null
  ))

  // Jobs and versions are part of the selection tree as well, only leaf tasks can hold a supplier
  const tasks = useMemo(() => (
    selectedTasks.filter((task) => +task?.type?.uid > 0)
  ), [selectedTasks])

  // Selected tasks are flat, so the job/version they belong to is looked up on the order
  const taskContext = useMemo(() => {
    const map = {}

    for (const job of order?.tasks || []) {
      for (const version of job.versions || []) {
        for (const task of version.tasks || []) {
          map[task.uid] = {
            path: `${job.title || `Job ${job.uid}`} / ${versionLabel(version)}`,
            targetlanguageId: +version.targetlanguageId || 0,
          }
        }
      }
    }

    return map
  }, [order])

  const entries = useMemo(() => tasks.map((task) => ({
    uid: +task.uid,
    typeId: +task.type?.uid || 0,
    shortname: task.type?.shortname || task.type?.name || '?',
    path: taskContext[task.uid]?.path || '',
    targetlanguageId: taskContext[task.uid]?.targetlanguageId || 0,
    supplier: assignedSupplier(task),
  })), [tasks, taskContext])

  const assignableIds = useMemo(() => (
    entries.filter(({ supplier }) => !supplier).map(({ uid }) => uid)
  ), [entries])

  const [checkedTasks, setCheckedTasks] = useState(assignableIds)

  const checkedEntries = useMemo(() => (
    entries.filter(({ uid }) => checkedTasks.includes(uid))
  ), [entries, checkedTasks])

  // Product Info and the supplier type filter are per task type, so they only
  // apply when the whole selection is one type - same rule as the single assign
  const sharedType = useMemo(() => {
    const typeIds = [...new Set(checkedEntries.map(({ typeId }) => typeId))]
    return typeIds.length === 1 ? checkedEntries[0] : null
  }, [checkedEntries])

  const sharedLanguageId = useMemo(() => {
    const ids = [...new Set(checkedEntries.map(({ targetlanguageId }) => targetlanguageId))]
    return ids.length === 1 ? ids[0] : 0
  }, [checkedEntries])

  const [fileSpecOptions] = useApi(sharedType
    ? `/rest/v2/task_filespecs?search[hidden]=0&search[deleted]=0&search[ordertasktype_uid]=${sharedType.typeId}`
    : null)

  useEffect(() => {
    setFileSpecs([])

    if (!sharedType) {
      setSupplierTypeIds([])
      return
    }

    fetch(`/rest/v2/task/${sharedType.uid}/supplier-types`)
      .then((response) => response.json())
      .then(({ data, success }) => setSupplierTypeIds(success ? data.supplierTypeIds : []))
      .catch(() => setSupplierTypeIds([]))
  }, [sharedType?.typeId, sharedType?.uid])

  // The cost is paid in the supplier's currency. A supplier with no currency on
  // their profile has to fall back to the order default - leaving the
  // previously picked supplier's currency in place books against the wrong one
  useEffect(() => {
    if (!supplier?.uid) {
      return
    }

    if (supplier.currency?.uid && supplier.currency?.iso) {
      setCostCurrency({ value: supplier.currency.uid, label: supplier.currency.iso })
      return
    }

    setCostCurrency(order?.customer?.currency
      ? { value: order.customer.currency.uid, label: order.customer.currency.iso }
      : null)
  }, [supplier])

  const checkTask = (uid) => setCheckedTasks((current) => (
    current.includes(+uid)
      ? current.filter((taskId) => taskId !== +uid)
      : [...current, +uid]
  ))

  const currentTimestamp = Math.floor(Date.now() / 1000)
  const oneDayInSeconds = 24 * 60 * 60

  const supplierIsUnavailable = supplier?.availability?.length
    ? supplier.availability.some(({ dateStart, dateEnd }) => {
        const start = parseInt(dateStart)
        const end = parseInt(dateEnd)
        return (
          (start < currentTimestamp && end > currentTimestamp) ||
          (start > currentTimestamp && start <= currentTimestamp + oneDayInSeconds)
        )
      })
    : false

  const serverDeadline = deadline ? toServerDateTime(deadline) : null
  const disabled = loading || !supplier?.uid || !checkedTasks.length

  const buildPayload = () => {
    const payload = {
      supplierId: supplier.uid,
      taskIds: checkedTasks,
    }

    // Only what was filled in - the back end leaves absent fields untouched
    if (brief.trim().length) {
      payload.externalnote = brief
    }

    if (`${cost}`.trim().length && +cost > 0) {
      payload.cost = cost
      payload.costCurrency = costCurrency?.value
    }

    if (`${price}`.trim().length && +price > 0) {
      payload.price = price
      payload.priceCurrency = priceCurrency?.value
    }

    if (serverDeadline) {
      payload.deadline = serverDeadline
    }

    if (fileSpecs.length) {
      payload.filespecs = fileSpecs.map(({ value }) => value)
    }

    return payload
  }

  const convertRate = async (fromCurrencyIso, toCurrencyIso, amount) => {
    const response = await fetch(`/rest/v2/currency/exchange`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ from: fromCurrencyIso, to: toCurrencyIso, amount }),
    })
    const jsonData = await response.json()

    if (!response.ok || typeof jsonData?.data?.amount === 'undefined') {
      throw new Error(
        jsonData?.errors?.join(', ') || `Could not exchange ${fromCurrencyIso} to ${toCurrencyIso}`
      )
    }

    return jsonData.data.amount
  }

  // Mirrors the single assign - a cost above the price needs a conscious yes
  const validateCostPriceRelation = async () => {
    if (!(+cost > 0) || !(+price > 0)) {
      return true
    }

    let comparableCost = parseFloat(cost)

    if (costCurrency?.value !== priceCurrency?.value) {
      try {
        comparableCost = parseFloat(
          await convertRate(costCurrency?.label, priceCurrency?.label, cost)
        )
      } catch (error) {
        // Without a rate the two cannot be compared, so let the coordinator decide
        return confirm(formatMessage(messages.conversionFailed, {
          costCurrency: costCurrency?.label,
          priceCurrency: priceCurrency?.label,
        }))
      }
    }

    if (comparableCost <= parseFloat(price)) {
      return true
    }

    return confirm('Cost is greater than Price.' + "\n\n" + 'Are you sure this is what you want?')
  }

  const describeChanges = () => {
    const changes = []

    if (brief.trim().length) {
      changes.push(formatMessage(messages.brief))
    }

    if (`${cost}`.trim().length && +cost > 0) {
      changes.push(`${formatMessage(messages.cost)}: ${cost} ${costCurrency?.label || ''}`.trim())
    }

    if (`${price}`.trim().length && +price > 0) {
      changes.push(`${formatMessage(messages.price)}: ${price} ${priceCurrency?.label || ''}`.trim())
    }

    if (serverDeadline) {
      changes.push(`${formatMessage(messages.deadline)}: ${serverDeadline}`)
    }

    if (fileSpecs.length) {
      changes.push(`${formatMessage(messages.productInfo)}: ${fileSpecs.map(({ label }) => label).join(', ')}`)
    }

    return changes.length
      ? formatMessage(messages.confirmChanges, { changes: `\n- ${changes.join("\n- ")}` })
      : formatMessage(messages.confirmNoChanges)
  }

  const onSubmit = async () => {
    if (disabled) {
      return
    }

    if (!await validateCostPriceRelation()) {
      return
    }

    // The notification goes out immediately, so give the coordinator a way back
    const confirmed = confirm(formatMessage(messages.confirmAssign, {
      supplier: supplier.userid,
      count: checkedTasks.length,
      changes: describeChanges(),
    }))

    if (!confirmed) {
      return
    }

    setLoading(true)

    fetch(`/rest/v2/task/bulkAssignSupplier`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildPayload()),
    })
      .then((response) => response.json())
      .then(({ success, errors = [], data = {} }) => {
        if (!success) {
          toastr.error(errors.join(', '))
          setLoading(false)
          return
        }

        if (data.skipped?.length) {
          toastr.warning(`${data.skipped.length} tasks were skipped, they already have a supplier`)
        }

        toastr.success(formatMessage(messages.assigned, { count: data.assigned?.length || 0 }))
        modal.resolve(data)
        modal.remove()
      })
      .catch((error) => {
        toastr.error(error)
        setLoading(false)
      })
  }

  return (
    <Modal
      show={modal.visible}
      onClose={() => modal.remove()}
      onOutsideClick={() => modal.remove()}
      onSubmit={onSubmit}
      title={formatMessage(messages.title)}
      dialogPanelClasses="max-w-2xl"
    >
      { !entries.length ? (
        <div className="text-sm text-gray-500">
          { formatMessage(messages.noTasks) }
        </div>
      ) : (
        <div className="space-y-4">
          <span className="block font-bold text-sm">{ formatMessage(messages.selectTasks) }</span>

          <div className="max-h-64 overflow-auto">
            <DataTable
              head={{
                selected: { label: '#', className: 'w-12' },
                task: { label: formatMessage(messages.task) },
                supplier: { label: formatMessage(messages.supplier) },
              }}
              body={{
                selected: (entry) => (
                  <Checkbox
                    disabled={!!entry.supplier}
                    onChange={() => checkTask(entry.uid)}
                    checked={checkedTasks.includes(+entry.uid)}
                  />
                ),
                task: (entry) => (
                  <div className="truncate">
                    <Chip size="xs" className="mr-2">{ entry.shortname }</Chip>
                    <span className="text-xs text-gray-500">{ entry.path }</span>
                  </div>
                ),
                supplier: (entry) => (
                  entry.supplier
                    ? <span className="text-xs">{ entry.supplier.userid }</span>
                    : <span className="text-xs text-gray-400">{ formatMessage(messages.unassigned) }</span>
                ),
              }}
              entries={entries}
            />
          </div>

          { !assignableIds.length &&
            <div className="text-sm text-gray-500">
              { formatMessage(messages.allAssigned) }
            </div>
          }

          <span className="block font-bold text-sm">{ formatMessage(messages.selectSupplier) }</span>

          { supplier?.commentsinternal?.length &&
            <Banner state="info" title={supplier.commentsinternal} />
          }

          { supplier?.uid && supplierIsUnavailable &&
            <Banner
              state="error"
              title={formatMessage(messages.supplierIsUnavailable, {
                unavailability: supplier.availability.map(({ dateStart, dateEnd, message }) => (
                  `${formatDate(new Date(dateStart * 1000))} - ${formatDate(new Date(dateEnd * 1000))}${message?.length ? ` - ${message}` : ''}`
                )).join("\n"),
              })}
            />
          }

          <SupplierSelect
            key={`supplierSelect-${supplierTypeIds}-${sharedLanguageId}`}
            required
            label={formatMessage(messages.supplier)}
            value={supplier ? { value: supplier.uid, label: supplier.userid } : null}
            onChange={(data) => setSupplier(data)}
            supplierTypeIds={supplierTypeIds}
            searchLanguageId={
              // The single assign skips the language filter for these types
              sharedLanguageId > 0 && !['AP', 'TR'].includes(sharedType?.shortname)
                ? sharedLanguageId
                : null
            }
          />

          <div>
            <span className="block font-bold text-sm">{ formatMessage(messages.details) }</span>
            <span className="block text-xs text-gray-500 mt-1">
              { formatMessage(messages.detailsDescription) }
            </span>
          </div>

          { +cost > 0 && supplier?.uid && !supplier?.currency?.iso &&
            <Banner
              state="warning"
              title={formatMessage(messages.supplierHasNoCurrency, {
                supplier: supplier.userid,
                costCurrency: costCurrency?.label,
              })}
            />
          }

          { +cost > 0 && supplier?.currency?.iso && costCurrency?.label !== supplier.currency.iso &&
            <Banner
              state="error"
              title={formatMessage(messages.currencyMismatch, {
                costCurrency: costCurrency?.label,
                supplierCurrency: supplier.currency.iso,
              })}
            />
          }

          <div className="flex justify-between items-start space-x-4">
            <div className="flex items-center w-1/3 space-x-2">
              <Input
                size="sm"
                type="number"
                label={formatMessage(messages.cost)}
                placeholder="Unchanged"
                value={cost}
                onChange={({ target: { value } }) => setCost(value)}
              />

              <SelectAsyncInline
                disabled={!roles.includes('finance')}
                className="mt-12"
                size="sm"
                value={costCurrency}
                onChange={(value) => setCostCurrency(value)}
                options={[]}
                cacheOptions
                defaultOptions
                loadOptions={loadCurrencies}
              />
            </div>

            <div className="flex items-center w-1/3 space-x-2">
              <Input
                size="sm"
                type="number"
                label={formatMessage(messages.price)}
                placeholder="Unchanged"
                value={price}
                onChange={({ target: { value } }) => setPrice(value)}
              />

              <SelectAsyncInline
                disabled={!roles.includes('finance')}
                className="mt-12"
                size="sm"
                value={priceCurrency}
                onChange={(value) => setPriceCurrency(value)}
                options={[]}
                cacheOptions
                defaultOptions
                loadOptions={loadCurrencies}
              />
            </div>

            <div className="w-1/3">
              <label className="text-gray-700 text-sm font-medium">
                { formatMessage(messages.deadline) }
              </label>

              <DateTime
                className="w-full"
                value={deadline}
                onChange={(value) => setDeadline(value)}
                inputProps={{
                  placeholder: 'Unchanged',
                  className: 'h-9 mt-2 text-center rounded-sm w-full border-t-gray-300 border',
                }}
                closeOnTab
                closeOnSelect
              />
            </div>
          </div>

          { !!fileSpecOptions?.length && sharedType &&
            <Select
              isMulti
              size="sm"
              label={formatMessage(messages.productInfo)}
              placeholder="Unchanged"
              value={fileSpecs}
              onChange={(value) => setFileSpecs(value || [])}
              options={fileSpecOptions.map(({ uid, name }) => ({ value: uid, label: name }))}
            />
          }

          { !sharedType && !!checkedEntries.length &&
            <span className="block text-xs text-gray-500">
              { formatMessage(messages.productInfoMixedTypes) }
            </span>
          }

          <MarkdownTextArea
            label={formatMessage(messages.brief)}
            description={formatMessage(messages.briefDescription)}
            value={brief}
            onChange={(value) => setBrief(value)}
          />

          <div className="w-full text-right">
            <Button onClick={onSubmit} disabled={disabled}>
              { loading
                ? formatMessage(messages.loading)
                : formatMessage(messages.submit, { count: checkedTasks.length }) }
            </Button>
          </div>
        </div>
      ) }
    </Modal>
  )
})
